import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, animate, useMotionValue, useSpring, useTransform, useReducedMotion, useInView } from "framer-motion";
import { ArrowRight, Handshake } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PanelAmbience, HC_STYLES } from "@/components/marketing/HeroCinematic";
import { SectionHeading } from "@/components/marketing/SectionHeading";

const EASE = [0.16, 1, 0.3, 1];
const CSS = `
.pcta-btn{position:relative;isolation:isolate;overflow:hidden;background:linear-gradient(100deg,#E9CE7A,#D4AF37 45%,#F1DDA4);color:#140F05;box-shadow:inset 0 1px 1px rgba(255,255,255,.7),0 14px 34px -12px rgba(212,175,55,.55);transition:transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s ease}
.pcta-btn:hover{transform:translateY(-2px);box-shadow:inset 0 1px 1px rgba(255,255,255,.8),0 20px 44px -12px rgba(212,175,55,.7)}
.pcta-btn::after{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.65) 50%,transparent 70%);transform:translateX(-120%);animation:pctaSweep 3.6s cubic-bezier(.4,0,.2,1) infinite}
@keyframes pctaSweep{0%,55%{transform:translateX(-120%)}100%{transform:translateX(120%)}}
.pcta-ring{position:absolute;inset:-6px;border-radius:9999px;border:1px solid rgba(212,175,55,.45);animation:pctaRing 2.8s ease-out infinite;pointer-events:none}
@keyframes pctaRing{0%{opacity:.7;transform:scale(1)}100%{opacity:0;transform:scale(1.18)}}
.pcta-btn:hover .pcta-arrow{transform:translateX(4px)}
.pcta-arrow{transition:transform .3s cubic-bezier(.16,1,.3,1)}
.pcta-disc{display:flex;flex-direction:column;align-items:center;justify-content:center;width:132px;height:132px;border-radius:9999px;position:relative;transform-style:preserve-3d;
  background:radial-gradient(circle at 35% 28%,rgba(251,239,196,.20),rgba(212,175,55,.07) 55%,rgba(20,16,10,.92) 100%);
  box-shadow:inset 0 1px 1px rgba(255,255,255,.3),inset 0 -6px 14px rgba(0,0,0,.5),0 24px 40px -18px rgba(0,0,0,.9),0 0 40px -16px rgba(212,175,55,.45);animation:pctaFloat 6s ease-in-out infinite}
.pcta-disc::before{content:"";position:absolute;inset:-1px;border-radius:inherit;padding:1px;background:conic-gradient(from 0deg,rgba(212,175,55,.12),#F6E3A6,rgba(212,175,55,.12) 40%,#D4AF37 70%,rgba(212,175,55,.12));-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;animation:pctaSpin 10s linear infinite}
@keyframes pctaFloat{0%,100%{transform:translateY(0) translateZ(24px)}50%{transform:translateY(-6px) translateZ(34px)}}
@keyframes pctaSpin{to{transform:rotate(360deg)}}
.pcta-disc--hero{box-shadow:inset 0 1px 1px rgba(255,255,255,.35),inset 0 -6px 14px rgba(0,0,0,.5),0 24px 40px -18px rgba(0,0,0,.9),0 0 54px -14px rgba(212,175,55,.6);transition:box-shadow .5s ease}
.pcta-disc--hero.is-peak{box-shadow:inset 0 1px 1px rgba(255,255,255,.45),inset 0 -6px 14px rgba(0,0,0,.45),0 24px 40px -18px rgba(0,0,0,.9),0 0 80px -6px rgba(246,227,166,.85)}
.pcta-disc--hero.is-peak .sh-accent{filter:drop-shadow(0 0 10px rgba(246,227,166,.7))}
.pcta-rise{position:absolute;inset:8px;border-radius:9999px;overflow:hidden;pointer-events:none}
.pcta-rise i{position:absolute;bottom:-6px;width:3px;height:3px;border-radius:9999px;background:#FBEFC4;box-shadow:0 0 6px #F6E3A6;opacity:0;animation:pctaUp 2.6s cubic-bezier(.7,0,.84,0) infinite}
.pcta-rise i:nth-child(1){left:22%;animation-delay:0s}.pcta-rise i:nth-child(2){left:40%;animation-delay:.5s}.pcta-rise i:nth-child(3){left:58%;animation-delay:1s}.pcta-rise i:nth-child(4){left:74%;animation-delay:1.5s}.pcta-rise i:nth-child(5){left:32%;animation-delay:2s}
@keyframes pctaUp{0%{transform:translateY(0);opacity:0}20%{opacity:.9}100%{transform:translateY(-130px);opacity:0}}
@media (max-width:640px){.pcta-disc{width:104px;height:104px}.pcta-ring{inset:-4px;animation-name:pctaRingSm}}
@keyframes pctaRingSm{0%{opacity:.6;transform:scale(1)}100%{opacity:0;transform:scale(1.05)}}
@media (prefers-reduced-motion: reduce){.pcta-btn::after,.pcta-ring,.pcta-disc,.pcta-disc::before,.pcta-rise i{animation:none}}
`;

const Disc = ({ big, label, delay, testId }) => (
  <motion.div style={{ transformStyle: "preserve-3d" }}
    initial={{ opacity: 0, y: 18, rotateX: 30 }} whileInView={{ opacity: 1, y: 0, rotateX: 0 }} viewport={{ once: true }} transition={{ duration: 1, ease: EASE, delay: 0.3 + delay * -0.2 }}>
    <div className="pcta-disc" style={{ animationDelay: `${delay}s` }}>
      <span data-no-translate data-testid={testId} className="sh-title sh-accent text-4xl sm:text-5xl leading-none">{big}</span>
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-2 text-center px-2">{label}</span>
    </div>
  </motion.div>
);

// £1 → £target, accelerating like a rocket, brief flash at the top, then loops.
const Skyrocket = ({ to, label, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  const reduce = useReducedMotion();
  const [v, setV] = useState(reduce ? to : 1);
  const [peak, setPeak] = useState(false);
  useEffect(() => {
    if (!inView || reduce) return undefined;
    let stop = false; let ctl; let tm;
    const run = () => {
      if (stop) return;
      setPeak(false);
      ctl = animate(1, to, { duration: 2.6, ease: [0.7, 0, 0.84, 0], onUpdate: setV,
        onComplete: () => { setPeak(true); tm = setTimeout(run, 1600); } });
    };
    run();
    return () => { stop = true; ctl && ctl.stop(); clearTimeout(tm); };
  }, [inView, reduce, to]);
  return (
    <motion.div ref={ref} style={{ transformStyle: "preserve-3d" }}
      initial={{ opacity: 0, y: 18, rotateX: 30 }} whileInView={{ opacity: 1, y: 0, rotateX: 0 }} viewport={{ once: true }} transition={{ duration: 1, ease: EASE, delay: 0.2 }}>
      <div className={`pcta-disc pcta-disc--hero ${peak ? "is-peak" : ""}`} style={{ animationDelay: "-0.8s" }}>
        <span aria-hidden className="pcta-rise"><i /><i /><i /><i /><i /></span>
        <span data-no-translate data-testid={testId} className="sh-title sh-accent text-4xl sm:text-[2.6rem] leading-none tabular-nums">£{Math.round(v).toLocaleString()}</span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-2 text-center px-2">{label}</span>
      </div>
    </motion.div>
  );
};

// Invitation to the Sales Partner Programme — open (no box), aligned with the page gutter, with depth.
export const PartnerCTA = () => {
  const { t } = useLang();
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/partners/content").then((r) => setC(r.data.commission)).catch(() => {}); }, []);
  const first = c ? `${c.first_month_rate}%` : "100%";
  const rate = c ? `${c.rate}%` : "30%";
  const reduce = useReducedMotion();
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 55, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 55, damping: 18 });
  const move = (e) => { if (reduce || e.pointerType !== "mouse") return; const r = e.currentTarget.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); };
  return (
    <section className="relative" data-testid="partner-cta-section" onPointerMove={move} onPointerLeave={() => { mx.set(0); my.set(0); }}>
      <style>{HC_STYLES + CSS}</style>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ WebkitMaskImage: "radial-gradient(closest-side at 60% 50%, #000 25%, transparent 100%)", maskImage: "radial-gradient(closest-side at 60% 50%, #000 25%, transparent 100%)" }}>
        <PanelAmbience />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14" style={{ perspective: 1400 }}>
        <motion.div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-14 items-center" style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
          <div style={{ transform: "translateZ(10px)" }}>
            <SectionHeading testId="partner-cta-heading" eyebrow="Sales Partner Programme" lead="Introduce venues." accent="Earn on every one." />
            <p className="text-white/65 mt-5 max-w-lg leading-relaxed">
              {t("Earn")} <span data-no-translate className="text-[#F1DDA4] font-semibold">{first}</span> {t("of the first month on every restaurant you bring in, then")} <span data-no-translate className="text-[#F1DDA4] font-semibold">{rate}</span> {t("every month they stay. Instant access — no test, no waiting.")}
            </p>
          </div>
          <div className="flex flex-col items-start lg:items-center gap-7" style={{ transformStyle: "preserve-3d" }}>
            <div className="flex flex-wrap gap-3 sm:gap-5" style={{ transformStyle: "preserve-3d" }}>
              <Skyrocket to={c ? c.per_new_customer : 350} label={t("Per new restaurant")} testId="partner-cta-skyrocket" />
              <Disc big={first} label={t("First month")} delay={0} testId="partner-cta-first" />
              <Disc big={rate} label={t("Every month after")} delay={-1.6} testId="partner-cta-rate" />
            </div>
            <motion.span className="relative inline-flex" style={{ transform: "translateZ(40px)" }}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}>
              <span className="pcta-ring" aria-hidden />
              <Link to="/partners" data-testid="become-sales-partner-btn"
                className="pcta-btn inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-[15px] font-semibold">
                <Handshake className="w-5 h-5" /> {t("Become a Sales Partner")} <ArrowRight className="pcta-arrow w-4 h-4" />
              </Link>
            </motion.span>
            <span className="text-xs text-white/50 font-mono-lux tracking-[0.2em] uppercase">{t("Uncapped · Paid monthly")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerCTA;
